import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { customerApi, userApi, areaPositionApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'

const KIND_LABELS = {
    homegroup: 'Homegroup',
    subcommittee: 'Subcommittee',
    area: 'Area',
    individual: 'Personal',
}

const KIND_COLORS = {
    homegroup: 'bg-blue-100 text-blue-800',
    subcommittee: 'bg-amber-100 text-amber-800',
    area: 'bg-purple-100 text-purple-800',
    individual: 'bg-emerald-100 text-emerald-800',
    other: 'bg-gray-100 text-gray-800',
}

/**
 * Customers — the single admin hub for groups AND their users.
 *
 * Each customer expands to show the users attached to it, where you can edit a
 * user, reset their password, enable/disable the login, or MOVE them (to a
 * different group, to a fresh personal account, or leave unassigned). An
 * "Unassigned" section at the bottom catches users with no group so nobody
 * becomes unmanageable.
 *
 * This page absorbs what used to be the separate Users page — it's fully
 * self-contained (the user modals live here), so Users.jsx can be removed.
 */
export default function Customers() {
    const { user: currentUser } = useAuth()
    const [customers, setCustomers] = useState([])
    const [users, setUsers] = useState([])
    const [positions, setPositions] = useState([])

    const [loading, setLoading] = useState(true)
    const [showInactiveCustomers, setShowInactiveCustomers] = useState(false)
    const [showDisabledUsers, setShowDisabledUsers] = useState(false)

    const [expandedId, setExpandedId] = useState(null)

    // Modals
    const [editingCustomer, setEditingCustomer] = useState(null)  // null | {} | customer
    const [editingUser, setEditingUser] = useState(null)          // null | {} | {customerId} | user
    const [resetTarget, setResetTarget] = useState(null)          // null | user
    const [moveTarget, setMoveTarget] = useState(null)            // null | user

    useEffect(() => { loadCustomers() }, [showInactiveCustomers])
    useEffect(() => { loadUsers() }, [showDisabledUsers])
    useEffect(() => { loadPositions() }, [])

    async function loadCustomers() {
        try {
            const response = await customerApi.list(showInactiveCustomers)
            if (response.success) setCustomers(response.customers)
            else toast.error(response.message || 'Could not load customers')
        } catch {
            toast.error('Network error loading customers')
        } finally {
            setLoading(false)
        }
    }

    async function loadUsers() {
        try {
            const response = await userApi.list(showDisabledUsers)
            if (response.success) setUsers(response.users)
        } catch { /* non-fatal */ }
    }

    async function loadPositions() {
        try {
            const response = await areaPositionApi.list()
            if (response.success) setPositions(response.positions)
        } catch { /* non-fatal */ }
    }

    function reloadAll() {
        loadCustomers()
        loadUsers()
    }

    // Group users under their customer (and collect the strays)
    const usersByCustomer = useMemo(() => {
        const map = new Map()
        const unassigned = []
        for (const u of users) {
            if (u.customerId == null) { unassigned.push(u); continue }
            if (!map.has(u.customerId)) map.set(u.customerId, [])
            map.get(u.customerId).push(u)
        }
        return { map, unassigned }
    }, [users])

    async function handleDeactivateCustomer(customer) {
        if (!confirm(`Deactivate "${customer.name}"? It's hidden from lists but order history is kept.`)) return
        try {
            const response = await customerApi.deactivate(customer.id)
            if (response.success) { toast.success(`${customer.name} deactivated`); reloadAll() }
            else toast.error(response.message || 'Could not deactivate')
        } catch { toast.error('Network error') }
    }

    async function handleDisableUser(user) {
        if (!confirm(`Disable login for "${user.name}"?`)) return
        try {
            const response = await userApi.deactivate(user.id)
            if (response.success) { toast.success('Login disabled'); loadUsers() }
            else toast.error(response.message || 'Could not disable')
        } catch { toast.error('Network error') }
    }

    async function handleReactivateUser(user) {
        const res = await userApi.update(user.id, { ...userPayload(user), enabled: true })
        if (res.success) { toast.success(`${user.name} reactivated`); loadUsers() }
        else toast.error(res.message || 'Could not reactivate')
    }

    async function handleDeleteUser(user) {
        if (!confirm(
            `Permanently delete "${user.name}"?\n\n` +
            `This can't be undone. Their past orders and reports stay in history ` +
            `(shown under their saved name), but the login is gone for good.\n\n` +
            `If you just want to stop them logging in, use Disable instead.`
        )) return
        try {
            const res = await userApi.deletePermanent(user.id)
            if (res.success) { toast.success(`${user.name} deleted`); reloadAll() }
            else toast.error(res.message || 'Could not delete user')
        } catch { toast.error('Network error') }
    }

    // Apply a move (called by the ReassignModal). target is one of:
    //   { kind: 'customer', id } | { kind: 'personal' } | { kind: 'unlink' }
    async function handleMoveApply(user, target) {
        let newCustomerId = null
        if (target.kind === 'customer') {
            newCustomerId = target.id
        } else if (target.kind === 'personal') {
            newCustomerId = await createPersonalCustomer(user)
            if (newCustomerId == null) return   // toast already shown
        } else {
            newCustomerId = null   // unlink
        }

        const res = await userApi.update(user.id, { ...userPayload(user), customerId: newCustomerId })
        if (res.success) {
            toast.success(target.kind === 'unlink' ? `${user.name} unassigned` : `${user.name} moved`)
            setMoveTarget(null)
            reloadAll()
        } else {
            toast.error(res.message || 'Could not move user')
        }
    }

    // Create an individual ("personal") customer for a user. Returns its id,
    // or null on failure (with a toast). Handles a name collision by trying a
    // suffixed name before giving up.
    async function createPersonalCustomer(user) {
        const attempts = [user.name, `${user.name} (personal)`]
        for (const name of attempts) {
            const res = await customerApi.create({ name, kind: 'individual' })
            if (res.success) return res.customer.id
            if (!(res.message || '').toLowerCase().includes('exist')) {
                toast.error(res.message || 'Could not create personal account')
                return null
            }
        }
        toast.error('That name is already taken. Rename the user or move them to a group instead.')
        return null
    }

    if (loading) {
        return <div className="p-8 text-center text-gray-500">Loading...</div>
    }

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-2xl font-semibold text-ghasc-navy">Customers</h2>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Groups and the people who order for them. Open a group to manage its users —
                        edit, reset a password, or move someone to another group or a personal account.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => setEditingUser({})}>+ New User</Button>
                    <Button onClick={() => setEditingCustomer({})}>+ New Customer</Button>
                </div>
            </div>

            <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center text-sm text-gray-600">
                    <input type="checkbox" checked={showInactiveCustomers}
                        onChange={(e) => setShowInactiveCustomers(e.target.checked)} className="mr-2" />
                    Show deactivated groups
                </label>
                <label className="flex items-center text-sm text-gray-600">
                    <input type="checkbox" checked={showDisabledUsers}
                        onChange={(e) => setShowDisabledUsers(e.target.checked)} className="mr-2" />
                    Show disabled logins
                </label>
            </div>

            {customers.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
                    No customers yet. Click "New Customer" to add the first one.
                </div>
            ) : (
                <div className="space-y-3">
                    {customers.map(c => {
                        const custUsers = usersByCustomer.map.get(c.id) || []
                        const expanded = expandedId === c.id
                        return (
                            <div key={c.id} className={`bg-white rounded-xl shadow-sm ${c.active ? '' : 'opacity-70'}`}>
                                <div className="p-4 flex items-start justify-between gap-4 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-gray-900 font-semibold break-words">{c.name}</span>
                                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${KIND_COLORS[c.kind] || KIND_COLORS.other}`}>
                                                {KIND_LABELS[c.kind] || c.kind}
                                            </span>
                                            {!c.active && (
                                                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                                                    Deactivated
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {custUsers.length} user{custUsers.length === 1 ? '' : 's'}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button
                                            onClick={() => setExpandedId(expanded ? null : c.id)}
                                            className="px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                                        >
                                            {expanded ? 'Hide users' : 'Manage users'}
                                        </button>
                                        <button
                                            onClick={() => setEditingCustomer(c)}
                                            className="px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                                        >
                                            Edit
                                        </button>
                                        {c.active && (
                                            <button
                                                onClick={() => handleDeactivateCustomer(c)}
                                                className="px-3 py-2 text-sm text-ghasc-danger bg-red-50 rounded-lg hover:bg-red-100"
                                            >
                                                Deactivate
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {expanded && (
                                    <div className="border-t border-gray-100 p-4 space-y-3">
                                        {custUsers.length === 0 ? (
                                            <div className="text-sm text-gray-500">
                                                No users in this group yet.
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {custUsers.map(u => (
                                                    <UserRow
                                                        key={u.id}
                                                        user={u}
                                                        isSelf={currentUser?.id === u.id}
                                                        onEdit={() => setEditingUser(u)}
                                                        onReset={() => setResetTarget(u)}
                                                        onMove={() => setMoveTarget(u)}
                                                        onDisable={() => handleDisableUser(u)}
                                                        onReactivate={() => handleReactivateUser(u)}
                                                        onDelete={() => handleDeleteUser(u)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        <button
                                            onClick={() => setEditingUser({ customerId: c.id })}
                                            className="text-sm text-ghasc-navy hover:underline"
                                        >
                                            + Add a user to {c.name}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Unassigned users */}
            {usersByCustomer.unassigned.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm">
                    <div className="px-4 py-3 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-ghasc-navy">Unassigned users</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Logins not tied to any group. Move them into a group or a personal account.
                        </p>
                    </div>
                    <div className="p-4 space-y-2">
                        {usersByCustomer.unassigned.map(u => (
                            <UserRow
                                key={u.id}
                                user={u}
                                unassigned
                                isSelf={currentUser?.id === u.id}
                                onEdit={() => setEditingUser(u)}
                                onReset={() => setResetTarget(u)}
                                onMove={() => setMoveTarget(u)}
                                onDisable={() => handleDisableUser(u)}
                                onReactivate={() => handleReactivateUser(u)}
                                onDelete={() => handleDeleteUser(u)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Modals */}
            {editingCustomer !== null && (
                <CustomerEditModal
                    customer={editingCustomer}
                    onClose={() => setEditingCustomer(null)}
                    onSaved={() => { setEditingCustomer(null); reloadAll() }}
                />
            )}

            {editingUser !== null && (
                <UserEditModal
                    user={editingUser}
                    customers={customers}
                    positions={positions}
                    onPositionsChanged={loadPositions}
                    onClose={() => setEditingUser(null)}
                    onSaved={() => { setEditingUser(null); reloadAll() }}
                />
            )}

            {resetTarget && (
                <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} />
            )}

            {moveTarget && (
                <ReassignModal
                    user={moveTarget}
                    fromCustomerName={moveTarget.customerName}
                    customers={customers}
                    onClose={() => setMoveTarget(null)}
                    onApply={(target) => handleMoveApply(moveTarget, target)}
                />
            )}
        </div>
    )
}

// Full UpdateUserRequest payload from an existing user row (everything except
// the field the caller overrides). Mirrors the shape the update endpoint wants.
function userPayload(user) {
    return {
        name: user.name,
        email: user.email,
        phone: user.phone,
        city: user.city,
        litRepName: user.litRepName,
        gsrName: user.gsrName,
        areaAdminName: user.areaAdminName,
        customerId: user.customerId,
        isSysAdmin: user.isSysAdmin,
        isAreaAdmin: user.isAreaAdmin,
        isGsr: user.isGsr,
        canUpload: user.canUpload,
        areaPosition: user.areaPosition,
        enabled: user.enabled,
    }
}

// ---------------------------------------------------------------------------
//  One user row inside a group (or the unassigned section)
// ---------------------------------------------------------------------------
function UserRow({ user, unassigned = false, isSelf = false, onEdit, onReset, onMove, onDisable, onReactivate, onDelete }) {
    return (
        <div className={`rounded-lg border border-gray-200 p-3 ${user.enabled ? '' : 'bg-gray-50 opacity-70'}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 break-words">{user.name}</span>
                        {!user.enabled && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                Disabled
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-gray-500 break-all">{user.email}</div>
                    <div className="mt-1"><RoleBadges user={user} /></div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                    <button onClick={onEdit}
                        className="px-2.5 py-1.5 text-xs text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100">Edit</button>
                    <button onClick={onReset}
                        className="px-2.5 py-1.5 text-xs text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100">Reset Pwd</button>
                    <button onClick={onMove}
                        className="px-2.5 py-1.5 text-xs text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100">
                        {unassigned ? 'Assign' : 'Move'}
                    </button>
                    {user.enabled ? (
                        <button onClick={onDisable}
                            className="px-2.5 py-1.5 text-xs text-ghasc-danger bg-red-50 rounded-lg hover:bg-red-100">Disable</button>
                    ) : (
                        <button onClick={onReactivate}
                            className="px-2.5 py-1.5 text-xs text-ghasc-success bg-green-50 rounded-lg hover:bg-green-100">Reactivate</button>
                    )}
                    {!isSelf && (
                        <button onClick={onDelete} title="Permanently delete this login"
                            className="px-2 py-1.5 text-xs text-gray-400 hover:text-ghasc-danger hover:underline">
                            Delete
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Move / assign modal
// ---------------------------------------------------------------------------
function ReassignModal({ user, fromCustomerName, customers, onClose, onApply }) {
    const isAssigning = !fromCustomerName
    const [mode, setMode] = useState('group')   // 'group' | 'personal' | 'unlink'
    const [targetId, setTargetId] = useState('')
    const [busy, setBusy] = useState(false)

    const options = customers.filter(c => c.active && c.id !== user.customerId)

    async function apply() {
        if (mode === 'group' && !targetId) { toast.error('Pick a group'); return }
        setBusy(true)
        let target
        if (mode === 'group') target = { kind: 'customer', id: Number(targetId) }
        else if (mode === 'personal') target = { kind: 'personal' }
        else target = { kind: 'unlink' }
        try {
            await onApply(target)   // parent shows toast + closes on success
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                <div className="p-6 space-y-4">
                    <div>
                        <h3 className="text-lg font-semibold text-ghasc-navy">
                            {isAssigning ? `Assign ${user.name}` : `Move ${user.name}`}
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {fromCustomerName ? `Currently in ${fromCustomerName}.` : 'Currently unassigned.'}
                        </p>
                    </div>

                    <div className="space-y-2">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="radio" name="move-mode" checked={mode === 'group'}
                                onChange={() => setMode('group')} className="mt-1" />
                            <div className="flex-1">
                                <div className="text-sm text-gray-900">Move to another group</div>
                                {mode === 'group' && (
                                    <select
                                        value={targetId}
                                        onChange={(e) => setTargetId(e.target.value)}
                                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                    >
                                        <option value="">— Select a group —</option>
                                        {options.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.name} ({KIND_LABELS[c.kind] || c.kind})
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </label>

                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="radio" name="move-mode" checked={mode === 'personal'}
                                onChange={() => setMode('personal')} className="mt-1" />
                            <div className="flex-1">
                                <div className="text-sm text-gray-900">Convert to a personal account</div>
                                <div className="text-xs text-gray-500">
                                    Creates an individual account in {user.name}'s name.
                                </div>
                            </div>
                        </label>

                        {!isAssigning && (
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input type="radio" name="move-mode" checked={mode === 'unlink'}
                                    onChange={() => setMode('unlink')} className="mt-1" />
                                <div className="flex-1">
                                    <div className="text-sm text-gray-900">Leave unassigned</div>
                                    <div className="text-xs text-gray-500">No group — they'll appear under "Unassigned".</div>
                                </div>
                            </label>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                        <Button type="button" onClick={apply} loading={busy}>
                            {isAssigning ? 'Assign' : 'Move'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Customer create / edit modal
// ---------------------------------------------------------------------------
function CustomerEditModal({ customer, onClose, onSaved }) {
    const isNew = !customer.id
    const [name, setName] = useState(customer.name || '')
    const [kind, setKind] = useState(customer.kind || 'homegroup')
    const [active, setActive] = useState(customer.active ?? true)
    const [saving, setSaving] = useState(false)

    async function handleSubmit(e) {
        e.preventDefault()
        if (!name.trim()) { toast.error('Name is required'); return }

        setSaving(true)
        try {
            const response = isNew
                ? await customerApi.create({ name: name.trim(), kind })
                : await customerApi.update(customer.id, { name: name.trim(), kind, active })
            if (response.success) {
                toast.success(isNew ? 'Customer created' : 'Customer updated')
                onSaved()
            } else {
                toast.error(response.message || 'Save failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-ghasc-navy">
                        {isNew ? 'New Customer' : `Edit: ${customer.name}`}
                    </h3>

                    <Input id="customer-name" label="Name" value={name}
                        onChange={(e) => setName(e.target.value)} required autoFocus />

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Kind</label>
                        <select
                            value={kind}
                            onChange={(e) => setKind(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                        >
                            <option value="homegroup">Homegroup</option>
                            <option value="subcommittee">Subcommittee</option>
                            <option value="area">Area</option>
                            <option value="individual">Personal</option>
                        </select>
                    </div>

                    {!isNew && (
                        <label className="flex items-center text-sm text-gray-700">
                            <input type="checkbox" checked={active}
                                onChange={(e) => setActive(e.target.checked)} className="mr-2" />
                            Active
                        </label>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                        <Button type="submit" loading={saving}>{isNew ? 'Create' : 'Save'}</Button>
                    </div>
                </form>
            </div>
        </div>
    )
}

// ===========================================================================
//  Role badges (from the old Users page)
// ===========================================================================
function RoleBadges({ user }) {
    const roles = []
    if (user.isSysAdmin) roles.push({ label: 'Sysadmin', color: 'bg-red-100 text-red-800' })
    if (user.isAreaAdmin) roles.push({
        label: user.areaPosition ? `Area: ${user.areaPosition}` : 'Area Admin',
        color: 'bg-blue-100 text-blue-800',
    })
    if (user.isGsr) roles.push({ label: 'GSR', color: 'bg-green-100 text-green-800' })
    if (user.canUpload) roles.push({ label: 'Upload', color: 'bg-purple-100 text-purple-800' })

    if (roles.length === 0) return <span className="text-xs text-gray-400">No special roles</span>
    return (
        <div className="flex flex-wrap gap-1">
            {roles.map((r, i) => (
                <span key={i} className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${r.color}`}>
                    {r.label}
                </span>
            ))}
        </div>
    )
}

// ===========================================================================
//  User create / edit modal (from the old Users page, unchanged behavior)
// ===========================================================================
function UserEditModal({ user, customers, positions, onPositionsChanged, onClose, onSaved }) {
    const isNew = !user.id
    const [form, setForm] = useState(() => ({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        city: user.city || '',
        password: '',
        customerId: user.customerId || '',
        litRepName: user.litRepName || '',
        gsrName: user.gsrName || '',
        areaAdminName: user.areaAdminName || '',
        isSysAdmin: !!user.isSysAdmin,
        isAreaAdmin: !!user.isAreaAdmin,
        isGsr: !!user.isGsr,
        canUpload: !!user.canUpload,
        areaPosition: user.areaPosition || '',
        enabled: isNew ? true : (user.enabled !== false),
    }))
    const [busy, setBusy] = useState(false)

    function update(field, value) {
        setForm(f => ({ ...f, [field]: value }))
    }

    const hasCustomer = !!form.customerId

    async function handleSave(e) {
        e.preventDefault()
        if (!form.name.trim() || !form.email.trim()) {
            toast.error('Name and email are required'); return
        }
        if (isNew && !form.password) {
            toast.error('Password is required for new users'); return
        }
        if (form.isAreaAdmin && !form.areaPosition) {
            toast.error('Please pick an area position'); return
        }

        setBusy(true)
        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim() || null,
                city: form.city.trim() || null,
                customerId: form.customerId === '' ? null : Number(form.customerId),
                litRepName: hasCustomer && form.litRepName.trim() ? form.litRepName.trim() : null,
                gsrName: form.isGsr && form.gsrName.trim() ? form.gsrName.trim() : null,
                areaAdminName: form.isAreaAdmin && form.areaAdminName.trim() ? form.areaAdminName.trim() : null,
                isSysAdmin: form.isSysAdmin,
                isAreaAdmin: form.isAreaAdmin,
                isGsr: form.isGsr,
                canUpload: form.canUpload,
                areaPosition: form.isAreaAdmin && form.areaPosition ? form.areaPosition.trim() : null,
                enabled: form.enabled,
            }
            if (isNew) payload.password = form.password

            const response = isNew
                ? await userApi.create(payload)
                : await userApi.update(user.id, payload)

            if (response.success) {
                toast.success(isNew ? 'User created' : 'User updated')
                onSaved()
            } else {
                toast.error(response.message || 'Save failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    async function handlePositionChange(value) {
        if (value === '__new__') {
            const name = prompt('New position name:')
            if (!name) return
            try {
                const response = await areaPositionApi.create({ name: name.trim() })
                if (response.success) {
                    toast.success(`Added "${response.position.name}"`)
                    update('areaPosition', response.position.name)
                    onPositionsChanged()
                } else {
                    toast.error(response.message || 'Could not add position')
                }
            } catch {
                toast.error('Network error')
            }
            return
        }
        update('areaPosition', value)
    }

    const positionOptionList = [...positions]
    const userHasLegacyPosition = form.areaPosition && !positions.find(p => p.name === form.areaPosition)
    if (userHasLegacyPosition) {
        positionOptionList.push({ id: 'legacy', name: form.areaPosition, _legacy: true })
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <form onSubmit={handleSave} className="bg-white rounded-xl shadow-xl w-full max-w-xl my-8">
                <div className="p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-ghasc-navy">
                        {isNew ? 'New User' : `Edit ${user.name}`}
                    </h3>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <Input id="u-name" label="Name" value={form.name} onChange={(e) => update('name', e.target.value)} required />
                        <Input id="u-email" label="Email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <Input id="u-phone" label="Phone (optional)" value={form.phone} onChange={(e) => update('phone', e.target.value)} />
                        <Input id="u-city" label="City (optional)" value={form.city} onChange={(e) => update('city', e.target.value)} />
                    </div>

                    {isNew && (
                        <Input id="u-password" label="Initial password" type="password"
                            value={form.password} onChange={(e) => update('password', e.target.value)} required />
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Customer / Homegroup</label>
                        <select
                            value={form.customerId}
                            onChange={(e) => update('customerId', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                        >
                            <option value="">— None (not linked) —</option>
                            {customers.map(c => (
                                <option key={c.id} value={c.id}>{c.name} ({c.kind})</option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Required for placing orders or submitting reports.</p>
                    </div>

                    {hasCustomer && (
                        <Input id="u-lit-rep-name" label="Lit Rep Name (optional)"
                            placeholder={`Defaults to "${form.name || 'the account name'}" if blank`}
                            value={form.litRepName} onChange={(e) => update('litRepName', e.target.value)} />
                    )}

                    <div className="border-t border-gray-100 pt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
                        <div className="space-y-2">
                            <RoleCheckbox checked={form.isSysAdmin} onChange={(v) => update('isSysAdmin', v)} label="Sysadmin"
                                hint="Full admin access — manage users, customers, inventory, config." />
                            <RoleCheckbox checked={form.isGsr} onChange={(v) => update('isGsr', v)} label="GSR"
                                hint="Group Service Representative — can submit monthly GSR reports." />
                            <RoleCheckbox checked={form.isAreaAdmin} onChange={(v) => update('isAreaAdmin', v)} label="Area Officer"
                                hint="Treasurer, Secretary, etc. — can submit area officer reports." />
                            <RoleCheckbox checked={form.canUpload} onChange={(v) => update('canUpload', v)} label="Inventory Upload"
                                hint="Can add/edit/import inventory items even if not a sysadmin." />
                        </div>
                    </div>

                    {form.isGsr && (
                        <Input id="u-gsr-name" label="GSR Name (optional)"
                            placeholder={`Defaults to "${form.name || 'the account name'}" if blank`}
                            value={form.gsrName} onChange={(e) => update('gsrName', e.target.value)} />
                    )}

                    {form.isAreaAdmin && (
                        <>
                            <Input id="u-area-admin-name" label="Area Admin Name (optional)"
                                placeholder={`Defaults to "${form.name || 'the account name'}" if blank`}
                                value={form.areaAdminName} onChange={(e) => update('areaAdminName', e.target.value)} />
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Area position <span className="text-ghasc-danger">*</span>
                                </label>
                                <select
                                    value={form.areaPosition}
                                    onChange={(e) => handlePositionChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                >
                                    <option value="">— Select position —</option>
                                    {positionOptionList.map(p => (
                                        <option key={p.id} value={p.name}>
                                            {p.name}{p._legacy ? ' (legacy)' : ''}
                                        </option>
                                    ))}
                                    <option value="__new__">+ New position…</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Manage the full list in Admin Config → Area Officer Positions.
                                </p>
                            </div>
                        </>
                    )}

                    {!isNew && (
                        <div className="border-t border-gray-100 pt-4">
                            <label className="flex items-center text-sm text-gray-900 cursor-pointer">
                                <input type="checkbox" checked={form.enabled}
                                    onChange={(e) => update('enabled', e.target.checked)} className="mr-2" />
                                Account enabled (can log in)
                            </label>
                            {!form.enabled && (
                                <p className="text-xs text-amber-700 mt-1">Unchecking will disable this user's login.</p>
                            )}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                        <Button type="submit" loading={busy}>{isNew ? 'Create' : 'Save'}</Button>
                    </div>
                </div>
            </form>
        </div>
    )
}

function RoleCheckbox({ checked, onChange, label, hint }) {
    return (
        <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5" />
            <div className="flex-1">
                <div className="text-sm text-gray-900">{label}</div>
                {hint && <div className="text-xs text-gray-500">{hint}</div>}
            </div>
        </label>
    )
}

// ===========================================================================
//  Reset password modal (from the old Users page, unchanged)
// ===========================================================================
function ResetPasswordModal({ user, onClose }) {
    const [pw1, setPw1] = useState('')
    const [pw2, setPw2] = useState('')
    const [busy, setBusy] = useState(false)

    async function handleSubmit(e) {
        e.preventDefault()
        if (pw1.length < 6) { toast.error('Password must be at least 6 characters'); return }
        if (pw1 !== pw2) { toast.error("Passwords don't match"); return }
        setBusy(true)
        try {
            const response = await userApi.resetPassword(user.id, pw1)
            if (response.success) { toast.success('Password reset'); onClose() }
            else toast.error(response.message || 'Reset failed')
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-xl w-full max-w-md">
                <div className="p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-ghasc-navy">Reset Password</h3>
                    <p className="text-sm text-gray-600">For: <span className="font-medium text-gray-900">{user.name}</span></p>

                    <Input id="pw1" label="New password" type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} required />
                    <Input id="pw2" label="Confirm password" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required />

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                        <Button type="submit" loading={busy}>Reset Password</Button>
                    </div>
                </div>
            </form>
        </div>
    )
}